// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import { getRecurringOrderTimesToCreate, utils } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import timezoneDayjs from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { ExitDialogButton } from '../../components/common/button';
import { useUserContext } from '../../context/UserContext';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '../../lib/base';
import { CUSTOMERS_QUERY } from '../../queries/CustomersQuery';
import { DASHBOARD_OVERDUE_TASKS_QUERY, DASHBOARD_QUERY } from '../../queries/DashboardQueries';
import type {
  CreateOverdueOrdersDataQuery,
  CreateOverdueOrdersDataQueryVariables,
  CreateOverdueOrdersMutation,
  CreateOverdueOrdersMutationVariables,
} from '../../types/__generated__/graphql';
import { createID, getTimeRanges } from '../../utils';
import SelectOverdueOrders from './SelectOverdueOrders';

dayjs.extend(utc);
dayjs.extend(timezoneDayjs);

interface Props {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly recurringOrderId: number;
}

const CREATE_OVERDUE_ORDERS_MUT = gql`
  mutation CreateOverdueOrders($id: Int!, $order_times_to_create: [Int!]!) {
    createRecurringOrderOverdueOrders(input: { recurring_order_id: $id, order_times_to_create: $order_times_to_create }) {
      orders_created {
        id
      }
      plantings_created {
        id
      }
      tasks_created {
        id
      }
    }
  }
`;
const RECURRING_ORDER_QUERY = gql`
  query CreateOverdueOrdersData($id: ID!) {
    recurring_order(id: $id) {
      id
      customer
      recurring_start_time
      recurring_end_time
      recurring_options {
        exceptions
        frequencyGroups {
          frequencyType
          frequencies
        }
      }
      inputs {
        edges {
          node {
            id
            crop {
              id
              total_grow_time
              harvest_buffer_time
            }
            blend {
              id
              crops {
                edges {
                  node {
                    id
                    crop {
                      id
                      total_grow_time
                      harvest_buffer_time
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    recurring_order_metadata(id: $id) {
        existing_order_times
    }
  }
`;

export default function CreateOverdueOrdersDialog({ isOpen, onClose, onSuccess, recurringOrderId }: Props) {
  const { timezone } = useUserContext();
  const { todayStart, sevenDaysFromTodayStart } = getTimeRanges(timezone);
  const [createOverdueOrders, { loading: createOverdueOrdersLoading }] = useMutation<
    CreateOverdueOrdersMutation,
    CreateOverdueOrdersMutationVariables
  >(CREATE_OVERDUE_ORDERS_MUT);
  const [getRecurringOrderData, { data, loading: recurringOrderDataLoading }] = useLazyQuery<
    CreateOverdueOrdersDataQuery,
    CreateOverdueOrdersDataQueryVariables
  >(RECURRING_ORDER_QUERY, {
    variables: { id: createID.recurringOrder(recurringOrderId) },
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });

  React.useEffect(() => {
    if (isOpen) {
      void getRecurringOrderData({
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, {
            variant: 'error',
            autoHideDuration: 10000,
          });
        },
      });
    }
  }, [isOpen, getRecurringOrderData]);

  const [selectedTimes, setSelectedTimes] = React.useState<number[]>([]);

  const onSubmit = () => {
    void createOverdueOrders({
      refetchQueries: [
        {
          query: DASHBOARD_QUERY,
          variables: {
            todayStart: todayStart.toString(),
            sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
          },
        },
        {
          query: DASHBOARD_OVERDUE_TASKS_QUERY,
          variables: {
            todayStart: todayStart.toString(),
            first: 25,
          },
        },
        { query: CUSTOMERS_QUERY },
      ],
      update: (cache, { data }) => {
        const plantingsCreated = (data?.createRecurringOrderOverdueOrders?.plantings_created?.length ?? 0) > 0;
        const tasksCreated = (data?.createRecurringOrderOverdueOrders?.tasks_created?.length ?? 0) > 0;
        cache.modify({
          fields: {
            harvests(existingGroup, { DELETE }) {
              return plantingsCreated ? DELETE : existingGroup;
            },
            plantings(existingGroup, { DELETE }) {
              return plantingsCreated ? DELETE : existingGroup;
            },
            task_groups(existingGroup, { DELETE }) {
              return tasksCreated ? DELETE : existingGroup;
            },
            tasks(existingTasks, { DELETE }) {
              return tasksCreated ? DELETE : existingTasks;
            },
            taskGroup(existingGroup, { DELETE }) {
              return tasksCreated ? DELETE : existingGroup;
            },
            plantingGroup(existingGroup, { DELETE }) {
              return plantingsCreated ? DELETE : existingGroup;
            },
            planting_groups(existingGroup, { DELETE }) {
              return plantingsCreated ? DELETE : existingGroup;
            },
            plantingTimeGroup(existingGroup, { DELETE }) {
              return plantingsCreated ? DELETE : existingGroup;
            },
            orders(_, { DELETE }) {
              return DELETE;
            },
          },
        });
      },
      variables: { id: recurringOrderId, order_times_to_create: selectedTimes },
      onCompleted: (completedData) => {
        enqueueSnackbar('Created overdue orders', { variant: 'success' });
        if (completedData.createRecurringOrderOverdueOrders != null) {
        }
        onSuccess();
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
      },
    });
  };

  const loading = recurringOrderDataLoading || createOverdueOrdersLoading;
  const submitDisabled = loading;

  const customer = data?.recurring_order?.customer;
  const inputs = data?.recurring_order?.inputs.edges.map(({ node }) => node) ?? [];

  const cropInputs = inputs
    .map((input) => input.crop)
    .filter(utils.notNull)
    .map((c) => c.total_grow_time + c.harvest_buffer_time);
  const blendInputs = inputs
    .map((input) => input.blend)
    .filter(utils.notNull)
    .flatMap((b) => b.crops.edges.map(({ node }) => node))
    .map((c) => {
      if (c.crop == null) {
        return Number.NEGATIVE_INFINITY;
      }

      return c.crop.total_grow_time + c.crop.harvest_buffer_time;
    });
  const longestTotalGrowTimePlusBuffer = Math.max(...cropInputs, ...blendInputs, 0);
  const alreadyCreatedOrderTimes = new Set(data?.recurring_order_metadata?.existing_order_times ?? []);
  // This is precautionary, extra filter- mainly for day light savings time changes
  const alreadyCreatedTimeDates = new Set(
    Array.from(alreadyCreatedOrderTimes).map((ts) => dayjs.unix(ts).tz(timezone).format('MM-DD-YYYY')),
  );

  let orderTimes: number[] = [];
  if (data?.recurring_order != null) {
    const canBeStartedInTime = (timeSeconds: number) => timeSeconds > todayStart + longestTotalGrowTimePlusBuffer;
    orderTimes = getRecurringOrderTimesToCreate({
      recurringStartTime: data.recurring_order.recurring_start_time,
      recurringEndTime: data.recurring_order.recurring_end_time ?? undefined,
      timezone,
      recurringOptions: data.recurring_order.recurring_options,
      longestTotalGrowTimePlusBuffer,
      ignoreGrowTimeLimit: true,
    })
      .filter((timeSeconds) => !canBeStartedInTime(timeSeconds))
      .filter((timeSeconds) => !alreadyCreatedOrderTimes.has(timeSeconds))
      .filter((timeSeconds) => !alreadyCreatedTimeDates.has(dayjs.unix(timeSeconds).tz(timezone).format('MM-DD-YYYY')));
  }

  const dialogContent = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }}>
      {orderTimes.length > 0 ? (
        <SelectOverdueOrders
          orders={orderTimes.map((time) => ({ time }))}
          timezone={timezone}
          selectedTimes={selectedTimes}
          setSelectedTimes={setSelectedTimes}
        />
      ) : (
        <Typography
          sx={{
            textAlign: 'center',
          }}
        >
          There are no overdue orders coming up!
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{`Create Overdue Orders for Recurring Order ${recurringOrderId}${customer ? ` (${customer})` : ''}`}</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>{dialogContent}</DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onClose}
          disabled={submitDisabled}
        >
          Cancel
        </Button>
        <Button
          id="submit-button"
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={submitDisabled || selectedTimes.length === 0}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
